import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { ScreenClassProvider } from '@vp/swan'
import { useIdentity } from '@vp/ubik-context'
import { Logger } from '@vp/ubik-logging'
import { type ExtendedMtsLocalizationResponse } from '@vp/ep-tiles'

// Pricing
import pricingContextModuleHelpers from '@vp/pricing-context-module/helpers'
import PricingContext from '../services/serviceClients/PricingContext'
import PcxtCookieProvider from '../utils/pcxtCookieProvider/pcxtCookieProvider'

import { ReviewsResponse } from '../types/Contentful/ContentfulTypes'
import { MsxMpv } from '../types/Msx'
import { DesignTemplateData } from '../types/DesignTemplate'
import { AuthProvider, Identity } from '../types/Auth'

export type AppContextProps = {
  auth: AuthProvider | undefined
  backgroundColor: string
  debugMode: boolean
  dictionaryEntries: Record<string, string>
  enableCampaignCallouts: boolean
  environment: string
  locale: string
  logger?: Logger
  merchandisingCategoryId: string
  reviewsData: ReviewsResponse | undefined
  uploadFlowMpv: MsxMpv | undefined
  sidebarEnabled: boolean
  vatInclusivity: boolean
  spaceId: string
  contentfulId: string
  experiments: Record<string, string | undefined>
  identity: Identity | undefined
  isPartOfDesignTemplateRollout: boolean
  designTemplateData: DesignTemplateData | undefined
  isPricingContextInitialized: boolean
  tenant: string
  calloutsLocalization: ExtendedMtsLocalizationResponse
}

const AppContext = createContext<AppContextProps>({
  auth: undefined,
  backgroundColor: 'platinum',
  debugMode: false,
  dictionaryEntries: {},
  enableCampaignCallouts: false,
  environment: '',
  locale: '',
  logger: undefined,
  merchandisingCategoryId: '',
  reviewsData: undefined,
  uploadFlowMpv: undefined,
  sidebarEnabled: false,
  vatInclusivity: false,
  spaceId: '',
  contentfulId: '',
  experiments: {},
  identity: undefined,
  isPartOfDesignTemplateRollout: false,
  designTemplateData: undefined,
  isPricingContextInitialized: false,
  tenant: '',
  calloutsLocalization: {}
})

interface AppProviderProps {
  backgroundColor: string | undefined
  children: ReactNode
  defaultVatIncl: boolean
  dictionaryEntries: Record<string, string>
  enableCampaignCallouts: boolean
  environment: string
  reviewsData: ReviewsResponse
  locale: string
  logger?: Logger
  merchandisingCategoryId: string
  sidebarEnabled: boolean
  uploadFlowMpv: MsxMpv | undefined
  spaceId: string
  contentfulId: string
  experiments: Record<string, string | undefined>
  isPartOfDesignTemplateRollout: boolean
  designTemplateData: DesignTemplateData | undefined
  isPricingContextInitialized?: boolean
  tenant: string
  calloutsLocalization: ExtendedMtsLocalizationResponse
}

const AppProvider = (props: AppProviderProps) => {
  const {
    backgroundColor,
    children,
    defaultVatIncl,
    dictionaryEntries,
    enableCampaignCallouts,
    environment,
    locale,
    logger,
    merchandisingCategoryId,
    reviewsData,
    sidebarEnabled,
    uploadFlowMpv,
    spaceId,
    contentfulId,
    experiments,
    isPartOfDesignTemplateRollout,
    designTemplateData,
    tenant,
    calloutsLocalization
  } = props
  const [debugMode, setDebugMode] = useState(false)
  const [identity, setIdentity] = useState(undefined)
  const [vatInclusivity, setVatInclusivity] = useState(defaultVatIncl)

  const isPricingContextInitialized = PricingContext.isInitialized()
  const ubikAuth = useIdentity()

  useEffect(() => {
    const newIdentity = ubikAuth.auth?.getIdentity?.()
    if (newIdentity !== identity) {
      setIdentity(newIdentity)
    }
  }, [ubikAuth])

  useEffect(() => {
    if (isPricingContextInitialized) {
      setVatInclusivity(PcxtCookieProvider.getVat(defaultVatIncl))
      // Event listeners for pricing context
      document.addEventListener(
        pricingContextModuleHelpers.EVENTS.INITIALIZED_EVENT,
        () => {
          setVatInclusivity(PcxtCookieProvider.getVat(defaultVatIncl))
        },
        { once: true }
      )
      document.addEventListener(
        pricingContextModuleHelpers.EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT,
        () => {
          setVatInclusivity(PcxtCookieProvider.getVat(defaultVatIncl))
        },
        false
      )
    }
  }, [isPricingContextInitialized])

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      const params = new URLSearchParams(window.location.search)
      setDebugMode(params.has('debugPage'))
    }
  }, [])

  const appContextValue = {
    auth: ubikAuth.auth,
    backgroundColor: backgroundColor || 'platinum',
    debugMode,
    dictionaryEntries,
    enableCampaignCallouts,
    environment,
    locale,
    logger,
    merchandisingCategoryId,
    reviewsData,
    uploadFlowMpv,
    sidebarEnabled,
    vatInclusivity,
    spaceId,
    contentfulId,
    experiments,
    identity,
    isPartOfDesignTemplateRollout,
    designTemplateData,
    isPricingContextInitialized,
    tenant,
    calloutsLocalization
  } as AppContextProps

  return (
    <AppContext.Provider value={appContextValue}>
      <ScreenClassProvider>{children}</ScreenClassProvider>
    </AppContext.Provider>
  )
}

const useAppContext = () => useContext(AppContext)

export { AppContext, AppProvider, useAppContext }
